import {
    setMediaPlanConfigValues,
    updateAddMediaPlanModalDisplay,
    updateShowNewMediaPlanDisplay,
} from "@/features/modalSlice";
import {
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
} from "antd";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    TMediaPlanConfig,
    TClientTags,
    DefaultMediaPlanLevel,
} from "@/ts/common-types";
import { getGenerateMediaPlanData } from "@/ts/retrieve-data";
import { compact, filter, map } from "underscore";
import { setIsGenerateMediaPlanLoading } from "@/features/loaderSlice";
import { setGenerateMediaPlanData, setIsMediaPlanCreate, setMediaPlanBudget } from "@/features/gridDataSlice";
import { MONTH_OPTIONS } from "@/ts/constants";
import { adjustOverallMediaPlanData } from "@/ts/utils";

type TAddMediaPlanModalProps = {
    show: boolean;
};

const AddMediaPlanModal: React.FC<TAddMediaPlanModalProps> = ({ show }) => {
    const dispatch = useDispatch();
    const [modalForm] = Form.useForm();

    const clientTagsList = useSelector(
        (state: { clientTagsList: { value: TClientTags[] } }) =>
            state.clientTagsList.value
    );

    const secondaryPlanLevels = useMemo(() => {
        return [
            "Campaign",
            ...map(
                filter(
                    clientTagsList,
                    (clientTag) =>
                        clientTag.tag_type !== DefaultMediaPlanLevel.CATEGORY
                ),
                (tagType) => tagType.tag_type
            ),
        ];
    }, [clientTagsList]);

    const handleModalDisplayChange = () => {
        dispatch(updateAddMediaPlanModalDisplay(false));
    };

    const [showOtherTagTypes, setShowOtherTagTypes] = useState(false);

    const onOtherTagTypesChange = (e: any) => {
        setShowOtherTagTypes(e.target.checked);
    };

    const onGeneratePlanClick = () => {
        modalForm
            .validateFields()
            .then(async (values: TMediaPlanConfig) => {
                const planLevelsToGenerate = compact(values.plan_level);
                dispatch(setIsGenerateMediaPlanLoading(true));
                dispatch(setMediaPlanConfigValues(values));
                dispatch(updateAddMediaPlanModalDisplay(false));
                dispatch(updateShowNewMediaPlanDisplay(true));
                dispatch(setMediaPlanBudget(+values.plan_budget));
                dispatch(setIsMediaPlanCreate(true));
                //get default data layout
                const mediaPlanData: any = await getGenerateMediaPlanData(
                    compact(planLevelsToGenerate)
                );

                const adjustedPlanData = adjustOverallMediaPlanData(
                    mediaPlanData,
                    +values.plan_budget
                );

                dispatch(setIsGenerateMediaPlanLoading(false));
                dispatch(setGenerateMediaPlanData(adjustedPlanData));
            })
            .catch((errorInfo) => {
                console.error("Validation Failed:", errorInfo);
            });
    };

    return (
        <Modal
            open={show}
            width={700}
            title="Create Media Plan"
            centered
            onCancel={handleModalDisplayChange}
            footer={
                <div style={{ textAlign: "left", marginLeft: 15 }}>
                    <Button
                        key="submit"
                        type="primary"
                        onClick={onGeneratePlanClick}
                    >
                        Generate
                    </Button>
                </div>
            }
        >
            <div className="add-goals-form-container">
                <Form
                    name="modalForm"
                    layout="vertical"
                    form={modalForm}
                    style={{ maxWidth: 600 }}
                    initialValues={{
                        plan_level: ["Account"],
                    }}
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Plan Name"
                                name="plan_name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please input plan name",
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Input plan name"
                                    className="border"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Budget"
                                name="plan_budget"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please input plan budget",
                                    },
                                ]}
                            >
                                <Input
                                    type="number"
                                    placeholder="Input plan budget"
                                    className="border"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Month"
                                name="plan_month"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a plan month",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select a month"
                                    allowClear
                                    options={MONTH_OPTIONS}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Planning For"
                                name="plan_level"
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            "Please select at least one plan level!",
                                    },
                                ]}
                            >
                                <Checkbox.Group>
                                    <Space direction="horizontal" size="large">
                                        {map(
                                            Object.values(
                                                DefaultMediaPlanLevel
                                            ),
                                            (defaultLevel, index) => (
                                                <Checkbox
                                                    value={defaultLevel}
                                                    key={defaultLevel}
                                                    disabled={
                                                        DefaultMediaPlanLevel.ACCOUNT ===
                                                        defaultLevel
                                                    }
                                                    className={`mx-${!index ? "2" : "4"
                                                        }`}
                                                >
                                                    {defaultLevel}
                                                </Checkbox>
                                            )
                                        )}
                                    </Space>
                                    <Space direction="horizontal" size="large">
                                        <Checkbox
                                            className="mx-2 mt-3"
                                            onChange={onOtherTagTypesChange}
                                        >
                                            Other Tag Types
                                        </Checkbox>
                                    </Space>

                                    {showOtherTagTypes && (
                                        <div
                                            style={{
                                                marginTop: "16px",
                                                padding: "10px",
                                                borderRadius: "10px",
                                                backgroundColor: "#f5f5f5",
                                                width: 600,
                                            }}
                                        >
                                            <Row gutter={32}>
                                                {map(
                                                    secondaryPlanLevels,
                                                    (level) => (
                                                        <Col span={6}>
                                                            <Checkbox
                                                                className="my-1"
                                                                key={level}
                                                                value={`${level}`}
                                                            >
                                                                {level}
                                                            </Checkbox>
                                                        </Col>
                                                    )
                                                )}
                                            </Row>
                                        </div>
                                    )}
                                </Checkbox.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
        </Modal>
    );
};

export default AddMediaPlanModal;
